import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  Calendar,
  CheckCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    service: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const services = [
    "Audiovisuel & Multimédia",
    "Sonorisation Salle de Conférence",
    "Domotique",
    "Éclairage Scénique",
    "Vidéo Surveillance",
    "Dépannage Fibre Optique",
    "Consultation Technique",
    "Autre"
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone",
      details: ["026 660 45 88", "079 614 84 79"],
      description: "Mardi: 9h-12h et 14h-17h, Jeudi: 14h-17h"
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@bt-connect.ch"],
      description: "Réponse sous 24h"
    },
    {
      icon: MapPin,
      title: "Adresse",
      details: ["Rue d'Yverdon 27", "1530 Payerne, Suisse"],
      description: "Sur rendez-vous uniquement"
    },
    {
      icon: Clock,
      title: "Horaire Show Room",
      details: ["Mardi: 9h00 - 12h00 et 14h00 - 17h00", "Jeudi: 14h00 - 17h00", "Lundi et Samedi matin: Sur RDV"],
      description: "Magasin: Mardi 9h-12h et 14h-17h, Jeudi 14h-17h. Lundi et Samedi matin sur RDV"
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Informations manquantes",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Envoyer le message via la nouvelle fonction Edge
      const { data, error } = await supabase.functions.invoke('contact_form_simple_20251219_1830', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          subject: formData.subject,
          message: formData.message
        }
      });

      if (error) {
        console.error('Erreur lors de l\'envoi:', error);
        toast({
          title: "Erreur d'envoi",
          description: "Une erreur s'est produite lors de l'envoi du message. Veuillez réessayer.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Message envoyé !",
        description: "Votre message a été envoyé avec succès. Nous vous contacterons rapidement.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
        service: ""
      });

    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: "Erreur d'envoi",
        description: "Une erreur s'est produite lors de l'envoi du message. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background to-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge variant="outline" className="mb-4">
              Contactez-Nous
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Parlons de Votre Projet
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Notre équipe d'experts est à votre disposition pour étudier vos besoins 
              et vous proposer des solutions sur mesure. Contactez-nous dès aujourd'hui !
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Tabs defaultValue="contact" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="partenaires">Nos Partenaires</TabsTrigger>
              </TabsList>
              
              <TabsContent value="contact">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center space-x-2">
                      <MessageSquare className="h-6 w-6" />
                      <span>Formulaire de Contact</span>
                    </CardTitle>
                    <CardDescription>
                      Remplissez ce formulaire et nous vous répondrons rapidement. 
                      Pour les urgences, appelez-nous directement.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Personal Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nom complet *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Votre nom complet"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="votre@email.ch"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            placeholder="026 660 45 88"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Entreprise</Label>
                          <Input
                            id="company"
                            value={formData.company}
                            onChange={(e) => setFormData({...formData, company: e.target.value})}
                            placeholder="Nom de votre entreprise"
                          />
                        </div>
                      </div>

                      {/* Service and Subject */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="service">Service concerné</Label>
                          <Select value={formData.service} onValueChange={(value) => setFormData({...formData, service: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez un service" />
                            </SelectTrigger>
                            <SelectContent>
                              {services.map((service) => (
                                <SelectItem key={service} value={service}>
                                  {service}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Sujet</Label>
                          <Input
                            id="subject"
                            value={formData.subject}
                            onChange={(e) => setFormData({...formData, subject: e.target.value})}
                            placeholder="Objet de votre demande"
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          placeholder="Décrivez votre projet ou votre demande..."
                          rows={6}
                          required
                        />
                      </div>

                      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                        <Send className="mr-2 h-4 w-4" />
                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Info Sidebar */}
              <div className="space-y-6">
                {/* Contact Details */}
                {contactInfo.map((info, index) => (
                  <Card key={index}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <info.icon className="h-5 w-5 text-primary" />
                        <span>{info.title}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="font-medium">{detail}</p>
                      ))}
                      <p className="text-sm text-muted-foreground">{info.description}</p>
                    </CardContent>
                  </Card>
                ))}

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Calendar className="h-5 w-5" />
                      <span>Actions Rapides</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full" variant="outline">
                      <Phone className="mr-2 h-4 w-4" />
                      Appeler Maintenant
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Calendar className="mr-2 h-4 w-4" />
                      Prendre RDV
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Mail className="mr-2 h-4 w-4" />
                      Email Direct
                    </Button>
                  </CardContent>
                </Card>

                {/* Response Time */}
                <Card>
                  <CardHeader>
                    <CardTitle>Temps de Réponse</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Email: Sous 24h</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Téléphone: Immédiat</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Devis: Sous 48h</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">Urgences: 24/7</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
              </TabsContent>
              
              <TabsContent value="partenaires">
                <div className="space-y-8">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold mb-4">Nos Partenaires de Confiance</h2>
                    <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                      Nous travaillons avec les meilleures marques du marché pour vous garantir 
                      des solutions de qualité supérieure et un service professionnel.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Rothen Connect Sarl */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">ROTHEN CONNECT SARL</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Spécialiste Photovoltaïque et Fibre Optique</p>
                      <p className="text-xs text-muted-foreground text-center">Gros déploiements et installations énergétiques</p>
                    </Card>
                    
                    {/* Sound & Light Effect */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">SOUND & LIGHT EFFECT</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Technique de Spectacle et Location</p>
                      <p className="text-xs text-muted-foreground text-center">Écran LED, location d'écrans LED et remorques</p>
                    </Card>
                    
                    {/* KYMO SA */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">KYMO SA</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Spécialiste Intégration Domotique</p>
                      <p className="text-xs text-muted-foreground text-center">Solutions domotiques intégrées et personnalisées</p>
                    </Card>
                    
                    {/* Ecranled.ch */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">ECRANLED.CH</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Spécialiste Location Écran LED Géant</p>
                      <p className="text-xs text-muted-foreground text-center">Location d'écrans LED et remorques LED</p>
                    </Card>
                    
                    {/* Agricam.ch */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">AGRICAM.CH</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Installateur Vidéosurveillance Agricole</p>
                      <p className="text-xs text-muted-foreground text-center">Surveillance spécialisée monde agricole</p>
                    </Card>
                    
                    {/* lightdesign.ch */}
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div className="h-16 flex items-center justify-center mb-4">
                        <div className="text-xl font-bold text-primary">LIGHTDESIGN.CH</div>
                      </div>
                      <p className="text-sm text-muted-foreground text-center mb-2">Spécialiste Projet LED sur Mesure</p>
                      <p className="text-xs text-muted-foreground text-center">Solutions d'éclairage LED personnalisées</p>
                    </Card>
                  </div>
                  
                  <div className="mt-12 text-center">
                    <Card className="p-8 bg-primary/5">
                      <h3 className="text-xl font-semibold mb-4">Pourquoi Ces Partenaires ?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        <div>
                          <h4 className="font-medium mb-2">Qualité Garantie</h4>
                          <p className="text-muted-foreground">Marques reconnues mondialement pour leur fiabilité et leurs performances</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Support Technique</h4>
                          <p className="text-muted-foreground">Formation continue et support technique direct des fabricants</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Innovation</h4>
                          <p className="text-muted-foreground">Accès aux dernières technologies et solutions du marché</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Notre Localisation</h2>
              <p className="text-muted-foreground">
                Situés au cœur de Paris, nous intervenons dans toute l'Île-de-France et au-delà
              </p>
            </div>
            
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <MapPin className="h-12 w-12 text-primary mx-auto" />
                    <p className="font-medium">Rue d'Yverdon 27</p>
                    <p className="text-muted-foreground">1530 Payerne, Suisse</p>
                    <Button variant="outline" className="mt-4" asChild>
                      <a href="https://www.google.com/maps/search/?api=1&query=Rue+d'Yverdon+27,+1530+Payerne,+Suisse" target="_blank" rel="noopener noreferrer">
                        Voir sur Google Maps
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;